import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Text } from './';
import { api, useAppSelector } from '../_library';
import { useIntl } from '../_library/intl';

// "Karšu pasūtījums" block of the Files page (Walletto companies only).
// A list can be uploaded, replaced while new and ordered once; nothing can be
// deleted. Ordering emails the file to Bonusukarte and is logged.

type CardOrderList = {
    id: number,
    fileName: string,
    status: 'new' | 'sent',
    size: number,
    mimeType: string | null,
    createdAt: string | null,
    orderedAt: string | null,
    orderedByEmail: string | null,
};

type OrderDetails = {
    fileName: string,
    size: number,
    companyName: string,
    email: string,
    phone: string,
};

const ACCEPT = '.xlsx,.xls';
const MAX_SIZE = 10 * 1024 * 1024;

const KNOWN_ERRORS = ['file_required', 'file_type_not_allowed', 'file_empty', 'file_too_large',
    'card_order_already_sent', 'card_order_not_found', 'card_orders_not_available'];

const errorLabel = (code?: string) => `bk.card_orders.error.${code && KNOWN_ERRORS.includes(code) ? code : 'general'}`;

const formatSize = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;

const fileType = (fileName: string) => (fileName.split('.').pop() || '').toUpperCase();

const ROLE_ORDER = ['accountant_readonly', 'accountant', 'manager', 'owner'];

const GreenButton = ({label, onClick, disabled}: {label: string, onClick: () => void, disabled?: boolean}) => {
    const intl = useIntl();
    return (
        <button type="button" disabled={disabled} onClick={onClick}
                className="rounded-md px-3 py-1.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 whitespace-nowrap">
            {intl.formatMessage({id: label})}
        </button>
    );
};

export const CardOrders = () => {
    const intl = useIntl();
    const { role } = useAppSelector(state => state.user);
    // accountant role or higher; switch to usePermission('employees', 'write') once section permissions are merged
    const canOrder = ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf('accountant');

    const [available, setAvailable] = useState(false);
    const [lists, setLists] = useState<CardOrderList[]>([]);
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [orderList, setOrderList] = useState<CardOrderList | null>(null);
    const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
    const [orderError, setOrderError] = useState('');
    const [isOrdering, setIsOrdering] = useState(false);

    const newFileInput = useRef<HTMLInputElement>(null);
    const replaceFileInput = useRef<HTMLInputElement>(null);
    const replaceListId = useRef<number | null>(null);

    const load = useCallback(() => {
        api.get('/cardOrders')
            .then(res => {
                setAvailable(!!res.data.available);
                setLists(res.data.lists || []);
            })
            .catch(() => setAvailable(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const sendFile = (file: File, url: string, successNotice: string) => {
        setError('');
        setNotice('');

        const extension = (file.name.split('.').pop() || '').toLowerCase();

        if (!['xlsx', 'xls'].includes(extension)) {
            setError(errorLabel('file_type_not_allowed'));
            return;
        }

        if (file.size > MAX_SIZE) {
            setError(errorLabel('file_too_large'));
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        setIsBusy(true);

        api.post(url, formData, {headers: {'Content-Type': 'multipart/form-data'}})
            .then(() => {
                setIsBusy(false);
                setNotice(successNotice);
                load();
            })
            .catch(e => {
                setIsBusy(false);
                setError(errorLabel(e?.response?.data?.error));
            });
    };

    const onNewFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (file) {
            sendFile(file, '/cardOrders', 'bk.card_orders.notice.uploaded');
        }
    };

    const onReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (file && replaceListId.current !== null) {
            sendFile(file, `/cardOrders/${replaceListId.current}/replace`, 'bk.card_orders.notice.replaced');
        }
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        if (file && canOrder && !isBusy) {
            sendFile(file, '/cardOrders', 'bk.card_orders.notice.uploaded');
        }
    };

    const download = (list: CardOrderList) => {
        api.get(`/cardOrders/${list.id}/file`, {responseType: 'blob'})
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', list.fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => setError(errorLabel('general')));
    };

    const openOrder = (list: CardOrderList) => {
        setOrderList(list);
        setOrderDetails(null);
        setOrderError('');

        api.get(`/cardOrders/${list.id}/orderDetails`)
            .then(res => setOrderDetails(res.data))
            .catch(e => setOrderError(errorLabel(e?.response?.data?.error)));
    };

    const confirmOrder = () => {
        if (orderList === null) {
            return;
        }

        setIsOrdering(true);
        setOrderError('');

        api.post(`/cardOrders/${orderList.id}/order`, {})
            .then(() => {
                setIsOrdering(false);
                setOrderList(null);
                setNotice('bk.card_orders.notice.ordered');
                load();
            })
            .catch(e => {
                setIsOrdering(false);
                setOrderError(errorLabel(e?.response?.data?.error));
                load();
            });
    };

    if (!available) {
        return null;
    }

    return (
        <div className="w-full mb-8">
            <div className="flex justify-between items-left mb-2">
                <Text label="bk.card_orders.title" className="text-lg font-semibold" />
            </div>

            {error ? <div className="bg-red-100 text-red-800 border border-red-200 rounded-md px-3 py-2 text-sm mb-2">{intl.formatMessage({id: error})}</div> : null}
            {notice ? <div className="bg-green-100 text-green-800 border border-green-200 rounded-md px-3 py-2 text-sm mb-2">{intl.formatMessage({id: notice})}</div> : null}

            <input ref={newFileInput} type="file" accept={ACCEPT} className="hidden" onChange={onNewFile} />
            <input ref={replaceFileInput} type="file" accept={ACCEPT} className="hidden" onChange={onReplaceFile} />

            <div className="bg-white rounded-lg shadow p-4">
                <ul className="space-y-1">
                    {lists.map(list => (
                        <li key={list.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded">
                            <div className="flex items-center space-x-4 w-full">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {list.fileName}
                                        <span className={'ml-2 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ' +
                                            (list.status === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700')}>
                                            {intl.formatMessage({id: `bk.card_orders.status.${list.status}`})}
                                        </span>
                                    </p>
                                    <p className="text-sm text-gray-500">{fileType(list.fileName)} • {formatSize(list.size)}</p>
                                    {list.status === 'sent' ?
                                        <p className="text-xs text-gray-600 mt-0.5">
                                            {intl.formatMessage({id: 'bk.card_orders.sent_by'},
                                                {email: list.orderedByEmail || '', date: list.orderedAt || ''})}
                                        </p>
                                        : null}
                                </div>
                                <Button label="bk.files.download" onClick={() => download(list)} extraSmall={true} color="plainGrey" />
                                {list.status === 'new' && canOrder ?
                                    <>
                                        <Button label="bk.card_orders.replace" extraSmall={true} color="plainGrey" disabled={isBusy}
                                                onClick={() => {
                                                    replaceListId.current = list.id;
                                                    replaceFileInput.current && replaceFileInput.current.click();
                                                }} />
                                        <GreenButton label="bk.card_orders.order" disabled={isBusy} onClick={() => openOrder(list)} />
                                    </>
                                    : null}
                            </div>
                        </li>
                    ))}
                </ul>

                {canOrder ?
                    <div
                        className={'flex items-center justify-between p-3 mt-1 rounded border-t border-dashed ' +
                            (isDragging ? 'bg-purple-50 border-purple-400' : 'border-gray-300')}
                        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={onDrop}
                    >
                        <Text left className="text-sm text-gray-500" label="bk.card_orders.new_list_hint" />
                        <Button label="bk.card_orders.add_list" extraSmall={true} color="plain" disabled={isBusy}
                                isLoading={isBusy}
                                onClick={() => newFileInput.current && newFileInput.current.click()} />
                    </div>
                    : !lists.length ? <Text label="bk.files.no_files" className="text-gray-500 py-4" /> : null}
            </div>

            <Modal isOpen={orderList !== null} toggle={() => !isOrdering && setOrderList(null)} title="bk.card_orders.confirm.title"
                content={
                    <div>
                        {orderError ? <div className="mb-3"><Text left error label={orderError} /></div> : null}
                        {orderDetails ?
                            <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 text-sm mb-4">
                                <dt className="text-gray-500">{intl.formatMessage({id: 'bk.card_orders.confirm.file'})}</dt>
                                <dd className="font-medium">{orderDetails.fileName} ({formatSize(orderDetails.size)})</dd>
                                <dt className="text-gray-500">{intl.formatMessage({id: 'bk.card_orders.confirm.company'})}</dt>
                                <dd className="font-medium">{orderDetails.companyName}</dd>
                                <dt className="text-gray-500">{intl.formatMessage({id: 'bk.card_orders.confirm.email'})}</dt>
                                <dd className="font-medium">{orderDetails.email}</dd>
                                <dt className="text-gray-500">{intl.formatMessage({id: 'bk.card_orders.confirm.phone'})}</dt>
                                <dd className="font-medium">{orderDetails.phone ? `+${orderDetails.phone.replace(/^\+/, '')}` : ''}</dd>
                            </dl>
                            : !orderError ? <Text left label="bk.card_orders.confirm.loading" /> : null}
                        <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-md px-3 py-2 text-sm">
                            {intl.formatMessage({id: 'bk.card_orders.confirm.warning'})}
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button secondary={true} label="bk.common.cancel" disabled={isOrdering} onClick={() => setOrderList(null)} />
                            <GreenButton label="bk.card_orders.confirm.submit" disabled={isOrdering || !orderDetails} onClick={confirmOrder} />
                        </div>
                    </div>
                }
            />
        </div>
    );
};
